// Sugalarm — token storage. Two parallel slots:
//   sugalarm.access  / sugalarm.refresh — JWT pair from /api/auth/login
//   sugalarm.deviceToken                — one-shot device token from
//                                         /api/pairing/poll/{id} on a kiosk
//
// `getBearer()` returns the device token first if present (kiosks always
// prefer it); otherwise the JWT. The backend's get_current_user accepts
// either, so the call site never has to dispatch on prefix.
//
// Cycle 41: `setJwt` / `clearAll` are the two seams the iOS Siri
// App-Intent auth bridge hooks into — every JWT write is mirrored into
// the shared App Group container so the out-of-webview intents can
// authenticate; every teardown wipes it. Both calls are fire-and-forget
// and no-op off the Capacitor native runtime, so the kiosk / web / PWA
// paths are byte-for-byte unchanged.

import { pushTokensToNative, clearTokensInNative } from "./authBridge";

const KEY_ACCESS = "sugalarm.access";
const KEY_REFRESH = "sugalarm.refresh";
const KEY_DEVICE_TOKEN = "sugalarm.deviceToken";
const KEY_DEVICE_ID = "sugalarm.deviceId";
const KEY_KIOSK = "sugalarm.kiosk";
// Cycle 190 ("Remember me", web-only): which storage the JWT pair lives in.
// Marker absent / "local" → localStorage (survives a browser restart — the
// pre-cycle default, so existing sessions are unchanged); "session" →
// sessionStorage (cleared when the tab/browser closes). The marker itself
// lives in localStorage so the rotation/adoptSession writer knows which
// store to write back to. ONLY the web login ever sets it; mobile/kiosk
// never call setJwtPersistMode, so they stay on localStorage byte-identically
// (the iOS App Group restore path is untouched).
const KEY_PERSIST = "sugalarm.jwtPersist";
// "The last thing that happened on this device was an explicit sign-out."
// A kiosk defaults its signed-out landing to /pair (a pairing code for the
// phone) — right for a fresh, unprovisioned unit, wrong the moment someone
// taps Log out: they want to sign back in, not read a code. This marker
// flips that one default to /login (App.tsx). It is set ONLY by the explicit
// sign-out path — never by an auth failure, so a revoked device token still
// lands on /pair to re-pair — and cleared the instant any session is
// established again (a JWT write or a completed pairing below), so a
// re-paired kiosk returns to the /pair cold-boot default.
const KEY_SIGNED_OUT = "sugalarm.signedOut";

export interface StoredJwt {
  access: string;
  refresh: string;
}

/** The store the JWT pair is WRITTEN to, per the persistence marker. */
function tokenStore(): Storage {
  try {
    return localStorage.getItem(KEY_PERSIST) === "session"
      ? sessionStorage
      : localStorage;
  } catch {
    return localStorage;
  }
}

/** Read a token from sessionStorage first, then localStorage (hydrate
 *  order) — so the session is found regardless of which store holds it. */
function readToken(key: string): string | null {
  try {
    return sessionStorage.getItem(key) ?? localStorage.getItem(key);
  } catch {
    return localStorage.getItem(key);
  }
}

/** Cycle 190: set the web "Remember me" persistence mode. `true` (default)
 *  → localStorage; `false` → sessionStorage. Call BEFORE setJwt at login so
 *  the tokens land in the right store. Never called on mobile/kiosk. */
export function setJwtPersistMode(remember: boolean): void {
  try {
    if (remember) localStorage.removeItem(KEY_PERSIST);
    else localStorage.setItem(KEY_PERSIST, "session");
  } catch {
    /* ignore */
  }
}

export function getJwt(): StoredJwt | null {
  const access = readToken(KEY_ACCESS);
  const refresh = readToken(KEY_REFRESH);
  if (!access || !refresh) return null;
  return { access, refresh };
}

export function setJwt(tokens: { access_token: string; refresh_token: string }): void {
  // A session exists again — drop the explicit-sign-out marker.
  clearSignedOutMarker();
  const store = tokenStore();
  const other = store === localStorage ? sessionStorage : localStorage;
  store.setItem(KEY_ACCESS, tokens.access_token);
  store.setItem(KEY_REFRESH, tokens.refresh_token);
  // Never leave a stale copy in the OTHER store shadowing this session.
  try {
    other.removeItem(KEY_ACCESS);
    other.removeItem(KEY_REFRESH);
  } catch {
    /* ignore */
  }
  // Cycle 41: mirror into the iOS App Group for the Siri App Intents.
  // Fire-and-forget; no-ops off-native. Cycle 190: unconditional — the
  // App Group write must NOT depend on the web-only persistence marker.
  pushTokensToNative(tokens);
}

/** Cycle 248 — re-mirror the CURRENT stored JWT pair into the iOS App
 *  Group on a signed-in app launch, not only on login/refresh. An
 *  already-signed-in user who simply updates the app (or whose original
 *  sign-in predated a working bridge build) loads the session straight from
 *  localStorage and never calls `setJwt`, so the App Group container the
 *  Siri App Intents read can sit empty/stale even though the webview is
 *  signed in — the intents then report "sign in first" forever. Calling
 *  this on every hydrate guarantees the container tracks the live session.
 *  No-ops off-native (via `pushTokensToNative`) and when there's no JWT
 *  (a device-token kiosk session or a signed-out app), so web/kiosk are
 *  byte-for-byte unchanged. */
export function mirrorJwtToNative(): void {
  const jwt = getJwt();
  if (!jwt) return;
  pushTokensToNative({ access_token: jwt.access, refresh_token: jwt.refresh });
}

export function clearJwt(): void {
  // Clear BOTH stores + the persistence marker on teardown.
  for (const s of [localStorage, sessionStorage]) {
    try {
      s.removeItem(KEY_ACCESS);
      s.removeItem(KEY_REFRESH);
    } catch {
      /* ignore */
    }
  }
  try {
    localStorage.removeItem(KEY_PERSIST);
  } catch {
    /* ignore */
  }
}

export function getDeviceToken(): string | null {
  return localStorage.getItem(KEY_DEVICE_TOKEN);
}

export function setDeviceToken(token: string, deviceId: string): void {
  // Pairing completed — this kiosk is provisioned again, so the
  // explicit-sign-out marker no longer describes it.
  clearSignedOutMarker();
  localStorage.setItem(KEY_DEVICE_TOKEN, token);
  localStorage.setItem(KEY_DEVICE_ID, deviceId);
}

export function clearDeviceToken(): void {
  localStorage.removeItem(KEY_DEVICE_TOKEN);
  localStorage.removeItem(KEY_DEVICE_ID);
}

export function getDeviceId(): string | null {
  return localStorage.getItem(KEY_DEVICE_ID);
}

/** Whichever bearer the client should send right now. Device tokens win
 *  on a kiosk; JWTs are the fallback for the phone/web app. */
export function getBearer(): string | null {
  // Device token (kiosk) wins; otherwise the JWT access token from whichever
  // store holds the session (sessionStorage first, then localStorage).
  return getDeviceToken() ?? readToken(KEY_ACCESS);
}

export function isKiosk(): boolean {
  return localStorage.getItem(KEY_KIOSK) === "1";
}

export function setKiosk(on: boolean): void {
  if (on) localStorage.setItem(KEY_KIOSK, "1");
  else localStorage.removeItem(KEY_KIOSK);
}

/** Record that the user explicitly signed out on this device. Called ONLY
 *  from `useAuth.signOut` — never from `clearAll()`, which also runs on an
 *  auth failure (a revoked kiosk device token must still land on /pair). */
export function markSignedOut(): void {
  try {
    localStorage.setItem(KEY_SIGNED_OUT, "1");
  } catch {
    /* ignore */
  }
}

export function clearSignedOutMarker(): void {
  try {
    localStorage.removeItem(KEY_SIGNED_OUT);
  } catch {
    /* ignore */
  }
}

/** True when the last session on this device ended in an explicit sign-out
 *  and nothing has signed in / paired since. The kiosk's signed-out landing
 *  reads this to show /login instead of a pairing code. */
export function wasSignedOut(): boolean {
  try {
    return localStorage.getItem(KEY_SIGNED_OUT) === "1";
  } catch {
    return false;
  }
}

/** True when the active bearer is a device token, false when it's a JWT
 *  (or when nothing is stored). Used by JWT-only screens to gate. */
export function isAuthedAsDevice(): boolean {
  return getDeviceToken() !== null;
}

/** Wipe everything. Used on hard signOut and on auth failure. */
export function clearAll(): void {
  clearJwt();
  clearDeviceToken();
  // Cycle 41: a signed-out phone must not be able to log a dose via
  // Siri — clear the App Group token container too. No-ops off-native.
  clearTokensInNative();
}

/** Cycle 25: map a frame variant to the `device_type` enum that
 *  /api/devices/register accepts. Kept here next to the rest of the
 *  auth/device storage helpers so the call site doesn't have to know
 *  the variant trinary. The backend's `ck_device_type` constraint
 *  accepts kiosk / web / mobile / other (cycle 4). */
export function getDeviceTypeForVariant(
  variant: "kiosk" | "web" | "mobile",
): "kiosk" | "web" | "mobile" | "other" {
  if (variant === "kiosk") return "kiosk";
  if (variant === "mobile") return "mobile";
  return "web";
}

/** Cycle 25: stable client_id stored in localStorage so repeat
 *  registrations of the same browser collapse into one device row.
 *  The kiosk path generates its own client_id during pairing; this
 *  is the JWT-session equivalent. */
const KEY_CLIENT_ID = "sugalarm.clientId";

export function getOrCreateClientId(): string {
  let id: string | null = null;
  try {
    id = localStorage.getItem(KEY_CLIENT_ID);
  } catch {
    /* ignore */
  }
  if (id) return id;
  id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  try {
    localStorage.setItem(KEY_CLIENT_ID, id);
  } catch {
    /* ignore */
  }
  return id;
}
